export interface DirectCallSpeech {
  messageId: string;
  turnId: string;
  seatId: string;
  text: string;
  conversationId?: string;
  eventTurnId?: number;
  humanTurnId?: number;
}

export interface DirectCallSpeechAdapter {
  // Resolve only when playback finishes; abort must stop playback and settle.
  speak(reply: DirectCallSpeech, signal: AbortSignal): Promise<void>;
}

export class DirectCallPlaybackInterrupted extends Error {
  constructor() {
    super('Audio playback cancelled by device interruption');
    this.name = 'DirectCallPlaybackInterrupted';
  }
}

export class DirectCallAudioController {
  private readonly queue = new DirectCallPlayback();
  private abort: AbortController | null = null;
  private running = false;
  private draining: Promise<void> = Promise.resolve();
  private captureHeld = false;
  private ended = false;
  private muted = false;
  private turnId: string | null = null;
  private turnGeneration: string | null = null;
  failure: { turnId: string; messageId: string } | null = null;
  get speaking(): boolean {
    return this.running;
  }
  /** Story 35513: replies waiting to be spoken after the one playing. */
  get queuedCount(): number {
    return this.queue.pendingCount;
  }
  // A reply is queued behind an open microphone (Story 34580). Never true
  // once voice processing lets capture and playback run at once (Story
  // 34588): there is nothing to yield to.
  get replyWaiting(): boolean {
    return (
      this.captureHeld && !this.voiceProcessingActive && this.queue.hasPending
    );
  }
  // Story 34588: true only once native voice processing (echo cancellation)
  // is active on the shared session, so capture and playback can safely run
  // at once. False (the default) keeps capture and playback mutually
  // exclusive exactly as in Story 34580.
  get voiceProcessingActive(): boolean {
    return this.voiceProcessingAvailable();
  }

  constructor(
    private readonly adapter: DirectCallSpeechAdapter,
    private readonly changed: () => void = () => undefined,
    private readonly voiceProcessingAvailable: () => boolean = () => false
  ) {}

  beginTurn(
    turnId: string,
    participants: readonly string[],
    generation: string = turnId
  ): void {
    this.queue.beginTurn(turnId, participants, generation);
    const isNewCall = this.turnId !== turnId;
    if (!isNewCall && this.turnGeneration === generation) return;
    this.turnId = turnId;
    this.turnGeneration = generation;
    this.failure = null;
    // Only a genuinely new call identity aborts in-flight playback. A new
    // human turn within the same call (generation change only) must not
    // interrupt a reply from an earlier turn that is already speaking or
    // queued (WI 34651): it queues behind it instead.
    if (isNewCall) this.abort?.abort();
    this.changed();
  }

  receive(reply: DirectCallSpeech): boolean {
    if (this.muted) return false;
    const accepted = this.queue.enqueue(reply);
    if (accepted) {
      this.scheduleDrain();
      this.changed();
    }
    return accepted;
  }

  /**
   * Story 35513 Skip: stop the reply playing now; the next queued one plays.
   * With nothing playing (a reply paused behind the open microphone), drop
   * the reply next in line instead (review 64015).
   */
  skip(): void {
    if (this.abort) this.abort.abort();
    else if (this.queue.dropNext()) this.changed();
  }

  /** Story 35513 clear: stop playback and drop every queued reply. */
  clearQueue(): void {
    this.queue.interrupt(true);
    this.abort?.abort();
    this.changed();
  }

  /** Barge-in or device interruption: scoped to the playing reply's turn. */
  interrupt(): void {
    this.queue.interrupt();
    this.abort?.abort();
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return;
    this.muted = muted;
    // Unmuting never replays a backlog from a turn muted by the operator;
    // mute drops every queued reply, a later human turn speaks again.
    this.queue.interrupt(true);
    this.abort?.abort();
  }

  end(): void {
    this.ended = true;
    this.queue.end();
    this.abort?.abort();
    this.failure = null;
    this.changed();
  }

  /** Share the same controller for capture and playback on every platform. */
  wrapInput(input: LocalSpeechInputAdapter): LocalSpeechInputAdapter {
    return {
      canPause: input.canPause,
      start: async (signal) => {
        if (this.captureHeld || this.ended || signal.aborted)
          throw new Error('Audio capture unavailable');
        this.captureHeld = true;
        // Story 35513: talking pauses the reply that is playing (so the
        // microphone never records it) and puts it back at the front of the
        // queue; it replays from the start once the turn is sent or the
        // microphone is cancelled. Nothing is dropped. With voice processing
        // active (Story 34588), capture and playback share one session and
        // run concurrently, so opening the microphone does not pause at all.
        if (this.running && !this.voiceProcessingActive) {
          this.queue.requeueSpeaking();
          this.abort?.abort();
          this.changed();
        }
        let released = false;
        const release = () => {
          if (released) return;
          released = true;
          this.captureHeld = false;
          this.scheduleDrain();
        };
        try {
          // Adapter settlement includes native stop, not only AbortSignal
          // delivery. With voice processing active, playback keeps draining
          // concurrently with capture (that promise may not resolve for the
          // rest of the call), so waiting on it here would block capture
          // indefinitely; only the mutually-exclusive path needs the wait.
          if (!this.voiceProcessingActive) await this.draining;
          if (signal.aborted || this.ended)
            throw new Error('Audio capture cancelled');
          const recording = await input.start(signal);
          if (signal.aborted || this.ended) {
            recording.cancel();
            throw new Error('Audio capture cancelled');
          }
          return {
            interrupted: recording.interrupted,
            interruptionCause: () => recording.interruptionCause?.() ?? null,
            inputLevel: () => recording.inputLevel?.() ?? 0,
            // US 35617 AC2: pass a false mute's pause/resume through.
            ...(recording.pause && recording.resume
              ? {
                  pause: () => recording.pause!(),
                  resume: () => recording.resume!(),
                }
              : {}),
            finish: async () => {
              try {
                return await recording.finish();
              } finally {
                release();
              }
            },
            cancel: () => {
              recording.cancel();
              release();
            },
          };
        } catch (error) {
          release();
          throw error;
        }
      },
    };
  }

  private scheduleDrain(): void {
    if (
      !this.running &&
      (!this.captureHeld || this.voiceProcessingActive) &&
      !this.ended
    )
      this.draining = this.drain();
  }

  private async drain(): Promise<void> {
    this.running = true;
    try {
      let reply: DirectCallSpeech | null;
      while (
        (!this.captureHeld || this.voiceProcessingActive) &&
        (reply = this.queue.next())
      ) {
        const abort = new AbortController();
        this.abort = abort;
        this.changed();
        try {
          await this.adapter.speak(reply, abort.signal);
        } catch (error) {
          if (
            error instanceof DirectCallPlaybackInterrupted &&
            !abort.signal.aborted &&
            this.turnId === reply.turnId
          ) {
            this.interrupt();
          } else if (!abort.signal.aborted) {
            this.failure = { turnId: reply.turnId, messageId: reply.messageId };
            this.changed();
          }
        } finally {
          this.queue.complete(reply.turnId, reply.messageId);
        }
      }
    } finally {
      this.abort = null;
      this.running = false;
    }
  }
}

// Client playback policy, not authorization. Only server-authorized replies
// may reach this queue; platform adapters must stop audio when interrupted.
export class DirectCallPlayback {
  private turnId: string | null = null;
  // WI 34651 follow-up: a stable per-call turnId (the conversation id) keeps
  // a reply to an older human turn from being dropped when a newer turn
  // exists, but that also means `turnId` alone no longer changes per human
  // turn. `generation` tracks the human-turn boundary separately: a
  // generation change with the SAME turnId releases interrupt/mute
  // suppression and the dedupe cap (a fresh turn deserves a fresh budget),
  // without touching `pending`/`speaking` - a reply already queued or
  // playing must never be dropped just because a newer turn began.
  private turnGeneration: string | null = null;
  // PR 4252 thread 51415: replies from several generations can share the
  // queue (an older reply still playing, a newer one queued behind it), so
  // suppression is scoped by a monotonic generation ordinal, not a boolean.
  // An interrupt suppresses generations up to and including the one it
  // interrupted, never a newer generation that is already queued.
  private ordinal = 0;
  private suppressedThrough = -1;
  private participants = new Set<string>();
  private seen = new Set<string>();
  private pending: QueuedSpeech[] = [];
  private speaking: QueuedSpeech | null = null;
  private closed = false;

  private get suppressed(): boolean {
    return this.ordinal <= this.suppressedThrough;
  }

  beginTurn(
    turnId: string,
    participants: readonly string[],
    generation: string = turnId
  ): void {
    if (this.closed) throw new Error('Conversation has ended');
    if (
      !turnId.trim() ||
      !participants.length ||
      participants.some((p) => !p.trim()) ||
      !generation.trim()
    ) {
      throw new Error('A turn and explicit participants are required');
    }
    this.participants = new Set(participants);
    if (turnId !== this.turnId) {
      this.turnId = turnId;
      this.pending = [];
      this.speaking = null;
    } else if (generation === this.turnGeneration) return;
    // A new generation (or call) releases suppression and the dedupe cap;
    // replies already queued or playing keep their own generation.
    this.turnGeneration = generation;
    this.ordinal++;
    this.seen.clear();
  }

  enqueue(reply: DirectCallSpeech): boolean {
    if (
      this.closed ||
      this.suppressed ||
      reply.turnId !== this.turnId ||
      !this.participants.has(reply.seatId) ||
      !reply.messageId.trim() ||
      !reply.text.trim() ||
      this.seen.has(reply.messageId) ||
      this.seen.size >= 64
    )
      return false;
    this.seen.add(reply.messageId);
    this.pending.push({ reply: { ...reply }, ordinal: this.ordinal });
    return true;
  }

  get hasPending(): boolean {
    return !this.closed && this.pending.length > 0;
  }

  get pendingCount(): number {
    return this.closed ? 0 : this.pending.length;
  }

  /** Drop the reply next in line; true when there was one (Story 35513). */
  dropNext(): boolean {
    if (this.closed || !this.pending.length) return false;
    this.pending.shift();
    return true;
  }

  /** Put the reply playing back at the front of the queue (Story 35513 pause). */
  requeueSpeaking(): void {
    if (!this.speaking) return;
    this.pending.unshift(this.speaking);
    this.speaking = null;
  }

  next(): DirectCallSpeech | null {
    if (this.closed || this.speaking) return null;
    this.speaking = this.pending.shift() ?? null;
    return this.speaking ? { ...this.speaking.reply } : null;
  }

  complete(turnId: string, messageId: string): boolean {
    if (
      this.speaking?.reply.turnId !== turnId ||
      this.speaking?.reply.messageId !== messageId
    ) {
      return false;
    }
    this.speaking = null;
    return true;
  }

  /**
   * Stop the reply that is playing and drop the rest of its generation and
   * any older one; a newer generation already queued keeps its place. With
   * nothing playing, or `everything` (mute), all queued speech is dropped.
   */
  interrupt(everything = false): void {
    const through = everything
      ? this.ordinal
      : (this.speaking?.ordinal ?? this.ordinal);
    this.suppressedThrough = Math.max(this.suppressedThrough, through);
    this.pending = this.pending.filter((item) => item.ordinal > through);
    this.speaking = null;
  }

  end(): void {
    this.closed = true;
    this.pending = [];
    this.speaking = null;
    this.participants.clear();
    this.seen.clear();
  }
}
interface QueuedSpeech {
  reply: DirectCallSpeech;
  ordinal: number;
}
import type { LocalSpeechInputAdapter } from './direct-call-input';
